import React, { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Expander, IconButton, Preview, useToggle, WaitingButton, ThemeName } from "@evergis/ui";

import { PrintSettings } from "./components/PrintSettings";
import { ThemeProvider } from "../../components/ThemeProvider";
import {
  PrintActions,
  PrintButton,
  PrintContainer,
  PrintHeader,
  PrintHeaderActionsDivider,
  PrintHeaderTitle,
  PrintMapControl,
  PrintWrapper,
} from "./styled";
import { setPrintEnabled } from "../../ducks/print/print";
import { getPrintEnabled } from "../../ducks/print/selectors";
import { usePrintDownload } from "./hooks/usePrintDownload";
import { usePrintPreview } from "./hooks/usePrintPreview";
import { useBoolSwitch } from "../../hooks/useBoolSwitch";

export const Print: FC = () => {
  const { t } = useTranslation("print");
  const dispatch = useDispatch();
  const [isOpenPreview, { setTrue: openPreview, setFalse: closePreview }] = useBoolSwitch();
  const { print, printWaiting } = usePrintDownload();
  const { preview, images, previewWaiting } = usePrintPreview(openPreview);
  const isEnabled = useSelector(getPrintEnabled);
  const [expanded, toggleExpanded] = useToggle(false);
  const closeDialog = useCallback(() => dispatch(setPrintEnabled(false)), []);

  if (!isEnabled) {
    return null;
  }

  return (
    <ThemeProvider themeName={ThemeName.Dark}>
      <PrintMapControl width={"22.5rem"}>
        <PrintWrapper>
          <PrintHeader>
            <PrintHeaderTitle>{t("exportImage")}</PrintHeaderTitle>
            <IconButton kind={"preview"} onClick={preview} />
            <IconButton kind={"download"} onClick={print} />
            <PrintHeaderActionsDivider vertical />
            <Expander onClick={toggleExpanded} isSelected={expanded} />
            <IconButton kind={"close"} onClick={closeDialog} />
          </PrintHeader>

          {expanded && (
            <PrintContainer>
              <PrintSettings />
              <PrintActions>
                <WaitingButton isWaiting={previewWaiting} onClick={preview}>
                  {t("preview")}
                </WaitingButton>
                <PrintButton isWaiting={printWaiting} onClick={print} primary>
                  {t("export")}
                </PrintButton>
              </PrintActions>
            </PrintContainer>
          )}
          <Preview images={images} isOpen={isOpenPreview} onClose={closePreview} />
        </PrintWrapper>
      </PrintMapControl>
    </ThemeProvider>
  );
};
